const UAParser = require('ua-parser-js');
const Algorithms = require('../ciphers/algorithms');
const BrowserJsCryptoService = require('./browser-js-crypto-service');
const BrowserNativeCryptoService = require('./browser-native-crypto-service');

const nativeSupport = BrowserNativeCryptoService.isSupported;
const NodeCryptoService = require('./node-crypto-service');
const errors = require('./errors');

// Minimum version of a browser to support the specified native implementation
// Note: These were pulled from http://caniuse.com/#search=crypto and verified
//       manually via saucelabs with the CryptoReporter lib via mocha.
const MIN_VERSION_SUPPORTED = {
  safari: {
    generateKeyPair: false,
    sha256: 8,
    cbcEncrypt: 8,
    gcmEncrypt: false,
    hmac: false,
    encryptWithPublicKey: false,
    generateInitializationVector: 9,
  },
  ie: {
    generateKeyPair: 13,
    sha256: 13,
    cbcEncrypt: 11,
    gcmEncrypt: false,
    hmac: false,
    encryptWithPublicKey: false,
    generateInitializationVector: 11,
  },
};

// Methods to check for a native implementation in runtime-specific contexts
let natives = Object.keys(MIN_VERSION_SUPPORTED.safari);

// Firefox and Chrome are trusted to not half-ass native crypto support
const runtime = _getRuntimeDetails();

// Default crypto service using SJCL
let cryptoService = BrowserJsCryptoService;
if (NodeCryptoService.isSupported) {
  cryptoService = NodeCryptoService;
}
if (nativeSupport) {
  cryptoService = BrowserNativeCryptoService;
}

// Add errors to service
cryptoService.EncryptionError = errors.EncryptionError;
cryptoService.CorruptTdfPackage = errors.CorruptTdfPackage;

// clones used to avoid cyclical references in symmetric crypto swap-outs
// NOTE: DO NOT MOVE THIS BELOW THE SWAP CODE IN THE NEXT STEP BELOW!!

const jsCryptoClone = { ...BrowserJsCryptoService };
const nativeCryptoClone = { ...BrowserNativeCryptoService };

// Selective native crypto replacements for IE/Safari
if (['safari', 'ie'].indexOf(runtime.name) >= 0 && nativeSupport) {
  // Check support for crypto methods in user-agent specified runtime
  natives = natives.reduce(crossReferenceSupport, {});

  // Symmetric crypto update if CBC _or_ GCM native are available
  // Note: Because CBC/GCM determination is internal, determine at invocation
  ['encrypt', 'decrypt'].forEach(function _symmetricCryptoSetup(method) {
    cryptoService[method] = (function methodDef(_method) {
      return function methodDefInner(payload, key, iv, algorithm, authTag) {
        const useGcm = algorithm === Algorithms.AES_256_GCM;
        return _swap(useGcm, _method)(payload, key, iv, algorithm, authTag);
      };
    })(method);
  });

  // Asymmetric crypto update
  ['encryptWithPublicKey', 'decryptWithPrivateKey'].forEach(function _asymmetricCryptoSetup(
    method
  ) {
    if (!natives.encryptWithPublicKey) {
      cryptoService[method] = BrowserJsCryptoService[method];
    }
  });

  // Specific crypto methods
  ['hmac', 'sha256', 'generateKeyPair', 'generateIntializationVector'].forEach(
    function _miscCryptoSetup(method) {
      if (!natives[method]) {
        cryptoService[method] = BrowserJsCryptoService[method];
      }
    }
  );

  if (!natives.generateInitializationVector) {
    cryptoService.generateKey = BrowserJsCryptoService.generateKey;
  }
}

// Determination of whether to use native crypto or SJCL
function _swap(useGcm, method) {
  // Use js crypto when:
  // - GCM crypto used and NOT natively supported
  // - GCM crypto NOT used and CBC native crypto NOT supported
  if ((useGcm && !natives.gcmEncrypt) || (!useGcm && !natives.cbcEncrypt)) {
    return jsCryptoClone[method];
  }
  return nativeCryptoClone[method];
}

function crossReferenceSupport(result, method) {
  result[method] = _checkFor(method);
  return result;
}

function _checkFor(method) {
  const runtimeMinimum = MIN_VERSION_SUPPORTED[runtime.name];
  const min = runtimeMinimum[method];
  return min ? runtime.version >= min : min;
}

// Determine runtime and version
function _getRuntimeDetails() {
  const userAgentParser = new UAParser();

  // Get runtime specifics
  const _runtime = userAgentParser.getBrowser();
  let name = _runtime.name && _runtime.name.toLowerCase();

  // For simplicity, ie/edge and safari/mobile-safari use same versioning scheme
  name = name === 'edge' ? 'ie' : name;
  name = name === 'mobile safari' ? 'safari' : name;

  // Get OS (chrome in IOS uses Safari's rendering engine with same crypto APIs)
  const os = (userAgentParser.getOS().name || '').toLowerCase();
  name = os === 'ios' ? 'safari' : name;

  // Get version major and minor as a float
  let version = _runtime.version && _runtime.version.toLowerCase();
  version = version && version.split('.').slice(0, 2).join('.');
  version = version && parseFloat(version, 10);

  return {
    version,
    name,
  };
}

module.exports = cryptoService;
